import { z } from 'zod';

/**
 * Incoming Item Master DTO — mirrors `03-database-spec.md` §4.7.
 */
export const incomingItemCategoryGroups = [
  'server',
  'ups',
  'cable',
  'hdd',
  'board',
  'metal_scrap',
  'plastic',
  'pallet',
  'mixed',
  'other',
] as const;

// Mirrors the DB enum `approval_status` defined in
// `docs/specs/03-database-spec.md` §3 and `backend/migrations/0001_enums.up.sql`.
// Phase 2 review (P1-#3) replaced the legacy {pending, withdrawn} pair with
// the canonical {draft, pending_approval, retired} values used by Postgres.
export const approvalStatusEnum = z.enum([
  'draft',
  'pending_approval',
  'approved',
  'rejected',
  'retired',
  'superseded',
]);

/**
 * The current sorting routes that can be set as the default for an
 * incoming item — mirrors `route_type` enum minus the `unresolved`
 * placeholder. The Sorting page uses this value to pre-fill the
 * route_type when the operator first classifies a package.
 */
export const standardRouteTypes = [
  'manual_dismantling',
  'material_sales',
  'storage_inventory',
] as const;
export type StandardRouteType = (typeof standardRouteTypes)[number];

/**
 * Retired values remain accepted on response DTOs so existing master records
 * keep rendering. They are deliberately excluded from every write schema.
 */
export const legacyReadableRouteTypes = [
  'secondary_process',
  // The light/heavy split, retired in migration 0032 — a master row set
  // before then still has to render.
  'manual_light_dismantling',
  'manual_heavy_dismantling',
] as const;
export const readableRouteTypes = [...standardRouteTypes, ...legacyReadableRouteTypes] as const;
export type ReadableRouteType = (typeof readableRouteTypes)[number];

export const incomingItemSchema = z.object({
  incoming_item_id: z.string(),
  japanese_name: z.string(),
  normalized_name: z.string(),
  category_group: z.enum(incomingItemCategoryGroups),
  active_flag: z.boolean(),
  effective_date: z.string(),
  approval_status: approvalStatusEnum,
  approved_by: z.string().nullable().optional(),
  approved_at: z.string().nullable().optional(),
  default_route_type: z.enum(readableRouteTypes).nullable().optional(),
});

export type IncomingItem = z.infer<typeof incomingItemSchema>;

/** Create-request payload — server fills server-managed columns. */
export const incomingItemCreateSchema = z.object({
  japanese_name: z.string().min(1),
  normalized_name: z.string().min(1),
  category_group: z.enum(incomingItemCategoryGroups),
  effective_date: z.string().min(1),
  default_route_type: z.enum(standardRouteTypes).nullable().optional(),
});

export type IncomingItemCreateInput = z.infer<typeof incomingItemCreateSchema>;

export type IncomingItemUpdateInput = Partial<IncomingItemCreateInput> & {
  active_flag?: boolean;
};

/** Body for `PUT /api/masters/incoming-items/:id/route`. */
export const incomingItemRouteUpdateSchema = z.object({
  default_route_type: z.enum(standardRouteTypes).nullable().optional(),
});
export type IncomingItemRouteUpdateInput = z.infer<typeof incomingItemRouteUpdateSchema>;
